import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

import {
  Card, Alert, Spinner, ProgressBar, useChartTheme, useIsMobile, monthAxes, monthChartHeight, compactTry,
} from '../../../core/ui';
import { useTranslation } from '../../../core/context/TranslationContext';

import { formatTry } from '../format';
import SpendingSummary from './SpendingSummary';
import SpendingToolbar from './SpendingToolbar';
import useSpendingReport from './useSpendingReport';
import { categoryName, percentOf } from './spendingFormat';
import styles from './SpendingReports.module.css';

/** Combined spending per category: monthly stacked chart plus the period breakdown. */
export default function SpendingByCategoryReport() {
  const { t, language } = useTranslation();
  const chart = useChartTheme();
  const vertical = useIsMobile();
  const { range, changeRange, scope, setScope, report, loading, error } = useSpendingReport();

  const categories = useMemo(() => report?.categories || [], [report]);
  const colorOf = (category, index) => category.color || chart.palette[index % chart.palette.length];

  const option = useMemo(() => {
    const months = report?.months || [];
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: (v) => formatTry(v),
        backgroundColor: chart.bgElevated,
        borderColor: chart.border,
        textStyle: { color: chart.text, fontSize: 12 },
      },
      legend: {
        type: 'scroll',
        textStyle: { color: chart.textMuted, fontSize: 12 },
        icon: 'circle',
      },
      grid: { left: '3%', right: '4%', bottom: vertical ? '3%' : '10%', containLabel: true },
      ...monthAxes(vertical, {
        type: 'category',
        data: months.map((m) => m.month),
        axisLabel: { color: chart.textMuted, fontSize: 11 },
        axisLine: { lineStyle: { color: chart.border } },
        axisTick: { show: false },
      }, {
        type: 'value',
        axisLabel: { color: chart.textMuted, fontSize: 11, formatter: compactTry },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: chart.border } },
      }),
      series: categories.map((c, i) => ({
        name: categoryName(c, t, language),
        type: 'bar',
        stack: 'total',
        barMaxWidth: 32,
        itemStyle: { color: c.color || chart.palette[i % chart.palette.length] },
        data: months.map((m) => Number(m.byCategory?.[c.key] || 0)),
      })),
    };
  }, [report, categories, chart, vertical, t, language]);

  const hasData = report && Number(report.total) > 0;

  return (
    <Card>
      <Card.Header>
        <div className={styles.headerRow}>
          <h5 className={styles.title}>{t('budget.reportSpendingByCategory')}</h5>
          <SpendingToolbar range={range} onRangeChange={changeRange} scope={scope} onScopeChange={setScope} />
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading && <Spinner size="sm" label={t('loading')} />}
        {!loading && !error && !hasData && <div className={styles.empty}>{t('budget.spendingEmpty')}</div>}
        {!loading && hasData && (
          <>
            <SpendingSummary report={report} />
            <ReactECharts
              option={option}
              notMerge
              style={{ height: monthChartHeight(vertical, report.months.length, 380) }}
            />
            <div className={styles.sectionTitle}>{t('budget.spendingCategoryBreakdown')}</div>
            <ul className={styles.rows}>
              {categories.map((c, i) => (
                <li key={c.key} className={styles.row}>
                  <div className={styles.rowHead}>
                    <span className={styles.rowName}>
                      <span className={styles.swatch} style={{ background: colorOf(c, i) }} />
                      {categoryName(c, t, language)}
                    </span>
                    <span className={styles.rowAmount}>{formatTry(c.total)}</span>
                  </div>
                  <ProgressBar now={percentOf(c.total, report.total)} aria-hidden="true" />
                  <div className={styles.rowMeta}>
                    {t('budget.spendingShare', { percent: percentOf(c.total, report.total), count: c.count })}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
